// Code generated by `go generate ./internal/apicode`. DO NOT EDIT.
//
// Source of truth: backend/internal/apicode/registry.go (ADR 0013).
// The generator renders this file from `apicode.Registry`;
// `apicode/generate_test.go` fails when the committed bytes differ from what
// the generator would write, so `go test ./...` cannot be green with a stale
// frontend list.
//
// To add a code: add the registry row, run `go generate ./internal/apicode`,
// classify it in frontend/src/api/retry.ts, and commit both.
//
// Client-only codes (network_error, request_failed, unknown_error) are NOT in
// the manifest — they are synthesised in the browser and live in errors.ts.

/** How the frontend is allowed to react to a code (docs/plan/10 §7.2). */
export type ApiErrorRetryClass =
  /** Authoritative refusal. A retry cannot change the outcome. */
  | 'never'
  /** Transient. Retry with capped exponential backoff. */
  | 'bounded'
  /** Exactly one retry, after re-priming the CSRF cookie. */
  | 'after-reprime'
  /** Exactly one retry, after honouring `Retry-After`. */
  | 'after-retry-after'
  /** Not a retry: drop the cached list and refetch from the first page. */
  | 'refetch-list'
  /** Exactly one retry, after re-syncing the proof clock offset, the device-key handle, or the stream ticket. */
  | 'after-resync';

/** Stable, machine-readable error codes the backend can emit. */
export const ApiErrorCodes = {
  BadRequest: 'bad_request',
  Unauthorized: 'unauthorized',
  SessionExpired: 'session_expired',
  SessionInvalid: 'session_invalid',
  SessionRevoked: 'session_revoked',
  InvalidCredentials: 'invalid_credentials',
  Forbidden: 'forbidden',
  CsrfInvalid: 'csrf_invalid',
  ProofRequired: 'proof_required',
  ProofInvalid: 'proof_invalid',
  ProofStale: 'proof_stale',
  ProofReplayed: 'proof_replayed',
  StreamTicketInvalid: 'stream_ticket_invalid',
  TooEarly: 'too_early',
  DeviceKeyInvalid: 'device_key_invalid',
  DeviceKeyRequired: 'device_key_required',
  DomainNotAllowed: 'domain_not_allowed',
  FromNotAllowed: 'from_not_allowed',
  RemoteContentNotAllowed: 'remote_content_not_allowed',
  StepUpRequired: 'step_up_required',
  NotFound: 'not_found',
  Conflict: 'conflict',
  ConfirmationRequired: 'confirmation_required',
  RulePinned: 'rule_pinned',
  AdminLockoutRisk: 'admin_lockout_risk',
  FolderInvalidated: 'folder_invalidated',
  StoredCredentialInvalid: 'stored_credential_invalid',
  StoredCredentialRequired: 'stored_credential_required',
  IdempotencyKeyReused: 'idempotency_key_reused',
  LabelExists: 'label_exists',
  LabelLimit: 'label_limit',
  LabelBusy: 'label_busy',
  LabelNameTooLong: 'label_name_too_long',
  DraftChanged: 'draft_changed',
  UnknownLabel: 'unknown_label',
  LabelsNotSupported: 'labels_not_supported',
  NextcloudURLInvalid: 'nextcloud_url_invalid',
  NextcloudHostBlocked: 'nextcloud_host_blocked',
  NextcloudNotLinked: 'nextcloud_not_linked',
  NextcloudLinkExpired: 'nextcloud_link_expired',
  NextcloudServerMismatch: 'nextcloud_server_mismatch',
  NextcloudRejected: 'nextcloud_rejected',
  NextcloudUnreachable: 'nextcloud_unreachable',
  NextcloudRefused: 'nextcloud_refused',
  NextcloudSharingDisabled: 'nextcloud_sharing_disabled',
  NextcloudQuotaExceeded: 'nextcloud_quota_exceeded',
  NextcloudUploadTooLarge: 'nextcloud_upload_too_large',
  NextcloudFolderTooLarge: 'nextcloud_folder_too_large',
  NextcloudNotFound: 'nextcloud_not_found',
  PushNotSubscribed: 'push_not_subscribed',
  SameAccount: 'same_account',
  UseOAuth: 'use_oauth',
  MessageTooLarge: 'message_too_large',
  AINotEnabled: 'ai_not_enabled',
  AIKeyRejected: 'ai_key_rejected',
  AIModelUnavailable: 'ai_model_unavailable',
  AIRefused: 'ai_refused',
  AINoCredit: 'ai_no_credit',
  AIInputTooLarge: 'ai_input_too_large',
  AIRateLimited: 'ai_rate_limited',
  AIOverloaded: 'ai_overloaded',
  AIUpstreamError: 'ai_upstream_error',
  RateLimited: 'rate_limited',
  Internal: 'internal_error',
  NotImplemented: 'not_implemented',
  UnsupportedByHost: 'unsupported_by_host',
  UnsupportedByServer: 'unsupported_by_server',
  UpstreamFailure: 'upstream_failure',
  UpstreamBusy: 'upstream_busy',
  AccountDegraded: 'account_degraded',
  UpstreamTimeout: 'upstream_timeout',
  OverQuota: 'over_quota',
} as const;

/** Union of every code the backend can emit. */
export type ApiErrorCode = (typeof ApiErrorCodes)[keyof typeof ApiErrorCodes];

/** Default HTTP status and retry class, one entry per manifest code. */
export const API_ERROR_CODE_META: Readonly<
  Record<ApiErrorCode, { readonly status: number; readonly retry: ApiErrorRetryClass }>
> = {
  bad_request: { status: 400, retry: 'never' },
  unauthorized: { status: 401, retry: 'never' },
  session_expired: { status: 401, retry: 'never' },
  session_invalid: { status: 401, retry: 'never' },
  session_revoked: { status: 401, retry: 'never' },
  invalid_credentials: { status: 401, retry: 'never' },
  forbidden: { status: 403, retry: 'never' },
  csrf_invalid: { status: 403, retry: 'after-reprime' },
  proof_required: { status: 401, retry: 'never' },
  proof_invalid: { status: 401, retry: 'after-resync' },
  proof_stale: { status: 401, retry: 'after-resync' },
  proof_replayed: { status: 401, retry: 'after-resync' },
  stream_ticket_invalid: { status: 401, retry: 'after-resync' },
  too_early: { status: 425, retry: 'after-resync' },
  device_key_invalid: { status: 400, retry: 'never' },
  device_key_required: { status: 403, retry: 'never' },
  domain_not_allowed: { status: 403, retry: 'never' },
  from_not_allowed: { status: 403, retry: 'never' },
  remote_content_not_allowed: { status: 403, retry: 'never' },
  step_up_required: { status: 403, retry: 'after-reprime' },
  not_found: { status: 404, retry: 'never' },
  conflict: { status: 409, retry: 'never' },
  confirmation_required: { status: 409, retry: 'never' },
  rule_pinned: { status: 409, retry: 'never' },
  admin_lockout_risk: { status: 409, retry: 'never' },
  folder_invalidated: { status: 409, retry: 'refetch-list' },
  stored_credential_invalid: { status: 409, retry: 'never' },
  stored_credential_required: { status: 409, retry: 'never' },
  idempotency_key_reused: { status: 409, retry: 'never' },
  label_exists: { status: 409, retry: 'never' },
  label_limit: { status: 409, retry: 'never' },
  label_busy: { status: 409, retry: 'never' },
  label_name_too_long: { status: 400, retry: 'never' },
  draft_changed: { status: 409, retry: 'never' },
  unknown_label: { status: 400, retry: 'never' },
  labels_not_supported: { status: 422, retry: 'never' },
  nextcloud_url_invalid: { status: 400, retry: 'never' },
  nextcloud_host_blocked: { status: 400, retry: 'never' },
  nextcloud_not_linked: { status: 409, retry: 'never' },
  nextcloud_link_expired: { status: 410, retry: 'never' },
  nextcloud_server_mismatch: { status: 409, retry: 'never' },
  nextcloud_rejected: { status: 409, retry: 'never' },
  nextcloud_unreachable: { status: 502, retry: 'bounded' },
  nextcloud_refused: { status: 502, retry: 'never' },
  nextcloud_sharing_disabled: { status: 409, retry: 'never' },
  nextcloud_quota_exceeded: { status: 413, retry: 'never' },
  nextcloud_upload_too_large: { status: 413, retry: 'never' },
  nextcloud_folder_too_large: { status: 422, retry: 'never' },
  nextcloud_not_found: { status: 404, retry: 'never' },
  push_not_subscribed: { status: 409, retry: 'never' },
  same_account: { status: 400, retry: 'never' },
  use_oauth: { status: 400, retry: 'never' },
  message_too_large: { status: 413, retry: 'never' },
  ai_not_enabled: { status: 409, retry: 'never' },
  ai_key_rejected: { status: 422, retry: 'never' },
  ai_model_unavailable: { status: 422, retry: 'never' },
  ai_refused: { status: 422, retry: 'never' },
  ai_no_credit: { status: 402, retry: 'never' },
  ai_input_too_large: { status: 413, retry: 'never' },
  ai_rate_limited: { status: 429, retry: 'never' },
  ai_overloaded: { status: 502, retry: 'never' },
  ai_upstream_error: { status: 502, retry: 'never' },
  rate_limited: { status: 429, retry: 'after-retry-after' },
  internal_error: { status: 500, retry: 'bounded' },
  not_implemented: { status: 501, retry: 'never' },
  unsupported_by_host: { status: 501, retry: 'never' },
  unsupported_by_server: { status: 501, retry: 'never' },
  upstream_failure: { status: 503, retry: 'bounded' },
  upstream_busy: { status: 503, retry: 'bounded' },
  account_degraded: { status: 503, retry: 'bounded' },
  upstream_timeout: { status: 504, retry: 'bounded' },
  over_quota: { status: 507, retry: 'never' },
} as const;

/** Every manifest code, in registry order. */
export const API_ERROR_CODE_LIST: readonly ApiErrorCode[] = Object.values(ApiErrorCodes);
